<template>
	<ChalkyUtilityModal
		class="view-modal-global-submit default-modal"
		v-bind:class="stateClass"
		v-model="isVisible"
		v-on:hide="Handle_OnHide"
	>
		<ChalkyUtilitySlideshow
			ref="slideshowView"
			v-bind:showDots="false"
			v-bind:showNavigation="false"
			v-bind:slideCount="3"
			v-on:change="Handle_OnSlideChange"
		>
			<!-- Slide 0: Search + Confirm -->
			<template #slide-0>
				<header class="stepped-overlay-header">
					<h3>Submit a Venue</h3>
					<p>Help grow the pool community</p>
				</header>

				<section class="primary-container stepped-overlay-content">
					<!-- Search Form -->
					<form
						class="location-search"
						v-if="!googlePlaceModel.uniqueKey"
						v-on:submit.prevent="Handle_OnSubmitSearch"
					>
						<fieldset>
							<label>
								<h6>Search for location</h6>

								<input
									autocomplete="name"
									minlength="6"
									name="name"
									ref="nameInput"
									required
									type="text"
									v-bind:placeholder="'e.g. Amsterdam Billiards, New York'"
									v-model="venueModel.attributes.name"
								/>
							</label>

							<section class="text-center">
								<p>Search with a name and location, like this:</p>

								<button
									class="button-secondary push-top"
									type="button"
									v-on:click="Handle_OnClickExample($event, 'Amsterdam Billiards, NY')"
								>
									Amsterdam Billiards, NY
								</button>

								<button
									class="button-secondary push-top"
									type="button"
									v-on:click="Handle_OnClickExample($event, 'Q1 Billiards, Canada')"
								>
									Q1 Billiards, Canada
								</button>

								<button
									class="button-secondary push-top"
									type="button"
									v-on:click="Handle_OnClickExample($event, 'Poolhouse, London')"
								>
									Poolhouse, London
								</button>
							</section>
						</fieldset>
					</form>

					<!-- Confirmed Location -->
					<section v-bind:key="googlePlaceModel.uniqueKey" v-else>
						<figure v-if="googlePlaceModel.photos">
							<img class="rounded" v-bind:src="googlePlaceModel.photos[0].url" />

							<figcaption class="pad-top pad-bottom text-center">
								<h3 class="name type-large-paragraph">{{ googlePlaceModel.getName() }}</h3>
								<p class="address color-chalky-grey-2 type-small-paragraph">
									{{ googlePlaceModel.getAddress() }}
								</p>
							</figcaption>
						</figure>
					</section>
				</section>

				<section class="action-container stepped-overlay-actions">
					<section v-if="googlePlaceModel.uniqueKey">
						<div class="button-group">
							<button class="button-secondary" v-on:click="Handle_OnClickCancel">Try Again</button>
							<button class="button-primary" v-on:click="Handle_OnClickContinue">Continue</button>
						</div>

						<p class="footnote stepped-overlay-footnote">
							If this is the spot, press Continue
							<br />
							to add details and submit.
						</p>
					</section>

					<section v-else>
						<div class="actions button-group">
							<img
								class="animate-spin filter-invert"
								height="24"
								src="~@chalkysticks/sass/build/asset/image/icon/loading.svg"
								v-bind:key="googlePlaceModel.uniqueKey"
								v-if="googlePlaceModel.loading"
								width="24"
							/>
							<button class="button-primary" v-else v-on:click="Handle_OnSubmitSearch">Search</button>
						</div>

						<p class="footnote stepped-overlay-footnote">
							For best results, enter the name of the
							<br />
							venue and the city it is located in.
						</p>
					</section>
				</section>
			</template>

			<!-- Slide 1: Venue Details -->
			<template #slide-1>
				<header class="stepped-overlay-header">
					<h3>Venue Details</h3>
					<p>Tell us more about {{ venueModel.getName() }}</p>
				</header>

				<section class="primary-container stepped-overlay-content details-container">
					<fieldset>
						<h5>Venue Details</h5>
						<label>
							<input class="switch" name="hasFood" type="checkbox" v-model="venueModel.hasFood" />
							<span>Do they serve food?</span>
						</label>
						<label>
							<input class="switch" name="cashOnly" type="checkbox" v-model="venueModel.cashOnly" />
							<span>Is it cash only?</span>
						</label>
						<label>
							<input class="switch" name="gambling" type="checkbox" v-model="venueModel.gambling" />
							<span>Does anyone gamble here?</span>
						</label>
						<label>
							<input class="switch" name="freeTable" type="checkbox" v-model="venueModel.freeTable" />
							<span>Are any of the tables free?</span>
						</label>
						<label>
							<input class="switch" name="livemusic" type="checkbox" v-model="venueModel.livemusic" />
							<span>Live music</span>
						</label>
						<label>
							<input class="switch" name="smoking" type="checkbox" v-model="venueModel.smoking" />
							<span>Smoking allowed indoors</span>
						</label>
					</fieldset>

					<fieldset>
						<h5 class="push-top">Events</h5>
						<label>
							<input class="switch" name="hasLeague" type="checkbox" v-model="venueModel.hasLeague" />
							<span>Do they have pool leagues here?</span>
						</label>
						<label>
							<input class="switch" name="tournaments" type="checkbox" v-model="venueModel.tournaments" />
							<span>Do they host tournaments?</span>
						</label>
					</fieldset>

					<fieldset>
						<h5 class="push-top">Equipment</h5>
						<label>
							<input class="switch" name="table7" type="checkbox" v-model="venueModel.table7" />
							<span>Do they have a 7' Table?</span>
						</label>
						<label>
							<input class="switch" name="table8" type="checkbox" v-model="venueModel.table8" />
							<span>Do they have a 8' Table?</span>
						</label>
						<label>
							<input class="switch" name="table9" type="checkbox" v-model="venueModel.table9" />
							<span>Do they have a 9' Table?</span>
						</label>
						<label>
							<input class="switch" name="snooker" type="checkbox" v-model="venueModel.snooker" />
							<span>Do they have a snooker table?</span>
						</label>
						<label>
							<input class="switch" name="billiards" type="checkbox" v-model="venueModel.billiards" />
							<span>Do they have a billiards table?</span>
						</label>
						<label>
							<input class="switch" name="pingPong" type="checkbox" v-model="venueModel.pingPong" />
							<span>Do they have a ping pong table?</span>
						</label>
						<label>
							<input class="switch" name="darts" type="checkbox" v-model="venueModel.darts" />
							<span>Do they have a dart board?</span>
						</label>
						<label>
							<input class="switch" name="foosball" type="checkbox" v-model="venueModel.foosball" />
							<span>Do they have a foosball table?</span>
						</label>
					</fieldset>

					<fieldset>
						<label style="margin-top: 0">
							<h6>Additional Notes</h6>

							<textarea
								name="notes"
								placeholder="Anything else you think we should know?"
								v-model="venueRevisionModel.attributes.notes"
							></textarea>
						</label>

						<label>
							<h6 class="push-top">Photo</h6>

							<input accept="image/*" name="image" type="file" v-on:change="Handle_OnChangeImage" />

							<p class="type-small-paragraph padded small">
								Upload a photo of the table, interior, or exterior if you have one.
							</p>
						</label>
					</fieldset>
				</section>

				<section class="action-container stepped-overlay-actions">
					<div class="button-group">
						<!-- Back to search is only meaningful when the user came through the search flow -->
						<button class="button-secondary" v-if="!isPreseeded" v-on:click="Handle_OnClickBack">
							Back
						</button>
						<button class="button-primary" v-bind:disabled="isLoading" v-on:click="Handle_OnClickSubmit">
							<span v-if="isLoading">Submitting...</span>
							<span v-else>Submit</span>
						</button>
					</div>

					<p class="footnote stepped-overlay-footnote">
						We will review your submission before it is published.
					</p>
				</section>
			</template>

			<!-- Slide 2: Confirmation -->
			<template #slide-2>
				<section class="primary-container stepped-overlay-content">
					<section class="complete-container stepped-overlay-complete text-center">
						<figure class="push-bottom">
							<img
								class="icon filter-invert"
								src="~@chalkysticks/sass/build/asset/image/icon/checkmark.svg"
							/>
						</figure>

						<h3>Thank you!</h3>

						<p>
							We appreciate you supporting ChalkySticks and helping us grow the sport of pool. We will
							review this shortly.
						</p>
					</section>
				</section>

				<section class="action-container stepped-overlay-actions">
					<div class="button-group">
						<button class="button-secondary size-medium" v-on:click="Handle_OnClickClose">Close</button>
					</div>
				</section>
			</template>
		</ChalkyUtilitySlideshow>
	</ChalkyUtilityModal>
</template>

<script lang="ts">
	import * as Service from '@/App/Service';
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Ref } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Global modal for submitting a new venue to ChalkySticks.
	 * Three steps: Search + Confirm → Venue Details → Confirmation
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Modal/Global
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewModalGlobalSubmit extends ViewBase {
		/**
		 * CSS state class based on the current step of the submission flow.
		 *
		 * @return string
		 */
		public get stateClass(): string {
			switch (this.currentStep) {
				case 0:
					return 'state-search';

				case 1:
					return 'state-details';

				case 2:
					return 'state-complete';

				default:
					return 'state-search';
			}
		}

		/**
		 * Reference to the slideshow view for navigation
		 *
		 * @type any
		 */
		@Ref('slideshowView')
		protected slideshowView!: any;

		/**
		 * Current step in the modal flow
		 *
		 * @type number
		 */
		protected currentStep: number = 0;

		/**
		 * Google Place model for location search
		 *
		 * @type ChalkySticks.Model.GooglePlace
		 */
		protected googlePlaceModel: ChalkySticks.Model.GooglePlace = ChalkySticks.Factory.GooglePlace.model();

		/**
		 * Selected image file for upload
		 *
		 * @type File | null
		 */
		protected imageFile: File | null = null;

		/**
		 * Whether a request is in progress
		 *
		 * @type boolean
		 */
		protected isLoading: boolean = false;

		/**
		 * Whether the modal is visible
		 *
		 * @type boolean
		 */
		protected isVisible: boolean = false;

		/**
		 * Whether the modal was opened with a pre-seeded venue (skipping the
		 * search step). Used to hide the Back button on the details slide so
		 * the user doesn't land on an empty search form.
		 *
		 * @type boolean
		 */
		protected isPreseeded: boolean = false;

		/**
		 * Current rotating placeholder text
		 *
		 * @type string
		 */
		protected placeholder: string = 'Tap a Keg, NY';

		/**
		 * Array of example venue names for rotating placeholder
		 *
		 * @type string[]
		 */
		protected placeholders: string[] = [
			'Tap a Keg, NY',
			'Cousins, OK',
			'Amsterdam Billiards',
			'Clicks, Orlando',
			'Eagle Billiards, PA',
		];

		/**
		 * Venue model for submission data
		 *
		 * @type ChalkySticks.Model.Venue
		 */
		protected venueModel: ChalkySticks.Model.Venue = ChalkySticks.Factory.Venue.model();

		/**
		 * Venue revision model for notes
		 *
		 * @type ChalkySticks.Model.VenueRevision
		 */
		protected venueRevisionModel: ChalkySticks.Model.VenueRevision = ChalkySticks.Factory.VenueRevision.model();

		/**
		 * Service for handling venue revision submissions
		 *
		 * @type Service.VenueRevision
		 */
		protected venueRevisionService: Service.VenueRevision;

		/**
		 * @constructor
		 */
		constructor() {
			super();

			this.venueRevisionService = new Service.VenueRevision(this);
		}

		/**
		 * Attach event listeners on mount.
		 *
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on('modal:submit:open', this.Handle_OnOpen);
			ChalkySticks.Utility.Interval.add(this.Handle_OnChangePlaceholder, 1000 * 5, 'submit-modal-placeholder');
		}

		/**
		 * Detach event listeners before destroy.
		 *
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off('modal:submit:open', this.Handle_OnOpen);
			ChalkySticks.Utility.Interval.remove('submit-modal-placeholder');
		}

		/**
		 * Close the modal and reset state.
		 *
		 * @return void
		 */
		protected close(): void {
			this.isVisible = false;
			this.reset();
		}

		/**
		 * Open the modal. If a venue model is provided, pre-populate the
		 * modal's venue data (cloned, so we don't mutate the caller's model)
		 * and jump straight to the details slide.
		 *
		 * @param ChalkySticks.Model.Venue | undefined venueModel
		 * @return void
		 */
		protected open(venueModel?: ChalkySticks.Model.Venue): void {
			this.reset();
			this.isVisible = true;

			// Pre-seeded venue -- skip search and go directly to the details form
			if (venueModel) {
				this.isPreseeded = true;
				this.venueModel.set(venueModel.toJSON());

				this.$nextTick(() => {
					this.slideshowView.goto(1);
				});
			}
		}

		/**
		 * Reset modal state to initial values.
		 *
		 * @return void
		 */
		protected reset(): void {
			this.currentStep = 0;
			this.googlePlaceModel.reset();
			this.imageFile = null;
			this.isLoading = false;
			this.isPreseeded = false;
			this.venueModel.reset();
			this.venueRevisionModel.reset();

			if (this.slideshowView) {
				this.slideshowView.goto(0);
			}
		}

		/**
		 * Handles venue submission errors with appropriate user messaging.
		 *
		 * @param number status
		 * @param string serverMessage
		 * @return void
		 */
		protected handleSubmissionError(status: number, serverMessage?: string): void {
			const messages: Record<number, string> = {
				400: 'Please fill in all required fields.',
				401: 'You must be logged in to submit a venue.',
				403: 'You are not allowed to submit a venue revision.',
				409: 'A revision matching these details already exists. Great minds think alike!',
				422: 'A revision matching these details already exists. Great minds think alike!',
			};

			if (serverMessage) {
				ChalkySticks.Core.Event.Bus.dispatch('dialog:alert', {
					message: serverMessage,
					title: 'Error',
				});
			} else {
				const message = messages[status] || 'An error occurred while submitting the venue.';

				ChalkySticks.Core.Event.Bus.dispatch('dialog:alert', {
					message: message,
					title: 'Error',
				});
			}
		}

		/**
		 * Run a venue search against GooglePlace using the given query,
		 * then sync the resolved place back onto the venue model.
		 *
		 * @param string query
		 * @return Promise<void>
		 */
		protected async search(query: string): Promise<void> {
			// Update the venue name so the input reflects the search
			this.venueModel.attributes.name = query;

			// Search geocode model
			this.googlePlaceModel.setQueryParam('address', query);

			// Fetch data
			await this.googlePlaceModel.fetch();

			console.log('Google Place result:', this.googlePlaceModel.place);

			// Update the Venue
			this.venueModel.set(this.googlePlaceModel.place);
		}

		/**
		 * Submit venue data to the server via the venue revision service.
		 *
		 * @return Promise<void>
		 */
		protected async submit(): Promise<void> {
			if (this.isLoading) {
				return;
			}

			// Check authentication
			if (!this.$store || !this.$store.getters['authentication/authenticated']) {
				ChalkySticks.Core.Event.Bus.dispatch('authentication:request');
				return;
			}

			this.isLoading = true;
			this.venueRevisionService = new Service.VenueRevision(this);

			const success = await this.venueRevisionService.submit(
				{
					additionalData: this.venueModel.toJSON(),
					imageFile: this.imageFile,
					notes: this.venueRevisionModel.attributes.notes,
				},
				{
					endpoint: this.venueRevisionModel.b.getUrl(),
					onError: (status: number, serverMessage?: string) => {
						this.handleSubmissionError(status, serverMessage);
					},
					onSuccess: () => {
						this.slideshowView.next();

						// Dispatch success event
						ChalkySticks.Core.Event.Bus.dispatch('venue:submit:complete', {
							venueModel: this.venueModel,
						});
					},
				},
			);

			this.isLoading = false;
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Rotates the search input placeholder text.
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnChangePlaceholder(): Promise<void> {
			const index = ChalkySticks.Utility.Math.randomInt(0, this.placeholders.length);

			this.placeholder = this.placeholders[index];
		}

		/**
		 * Handle image file selection.
		 *
		 * @param Event e
		 * @return Promise<void>
		 */
		protected async Handle_OnChangeImage(e: Event): Promise<void> {
			const target = e.target as HTMLInputElement;

			if (target && target.files && target.files.length > 0) {
				this.imageFile = target.files[0];
			} else {
				this.imageFile = null;
			}
		}

		/**
		 * Handle back button click - go to previous slide.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickBack(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.slideshowView.previous();
		}

		/**
		 * Handle cancel button click - reset search.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickCancel(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.googlePlaceModel.reset();
			this.venueModel.reset();
		}

		/**
		 * Handle close button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickClose(e?: PointerEvent): Promise<void> {
			e?.preventDefault();

			this.close();
		}

		/**
		 * Handle continue button click - advance to details slide.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickContinue(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.slideshowView.next();
		}

		/**
		 * Handle example button click - run the search with the provided example query.
		 *
		 * @param PointerEvent e
		 * @param string example
		 * @return Promise<void>
		 */
		protected async Handle_OnClickExample(e: PointerEvent, example: string): Promise<void> {
			e.preventDefault();

			await this.search(example);
		}

		/**
		 * Handle submit button click - submit venue data.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickSubmit(e: PointerEvent): Promise<void> {
			e.preventDefault();

			await this.submit();
		}

		/**
		 * Handle modal hide event.
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnHide(): Promise<void> {
			this.close();
		}

		/**
		 * Handle modal open event from event bus. Payload may carry a
		 * venueModel to pre-seed the details form and skip the search step.
		 *
		 * @param ChalkySticks.Core.IDispatcherEvent<{ venueModel?: ChalkySticks.Model.Venue }> e
		 * @return Promise<void>
		 */
		protected async Handle_OnOpen(
			e?: ChalkySticks.Core.IDispatcherEvent<{ venueModel?: ChalkySticks.Model.Venue }>,
		): Promise<void> {
			this.open(e?.data?.venueModel);
		}

		/**
		 * Handle slideshow slide change.
		 *
		 * @param number index
		 * @return Promise<void>
		 */
		protected async Handle_OnSlideChange(index: number): Promise<void> {
			this.currentStep = index;
		}

		/**
		 * Handle search form submission.
		 *
		 * @param Event e
		 * @return Promise<void>
		 */
		protected async Handle_OnSubmitSearch(e: Event): Promise<void> {
			e.preventDefault();

			await this.search(this.venueModel.attributes.name);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-modal-global-submit {
		.location-search {
			footer {
				margin: 0 auto;
				margin-top: 1rem;
				text-align: center;
			}
		}

		// additional notes
		textarea {
			min-height: 10rem;
		}
	}
</style>
